import type { SWRConfiguration } from "swr";
import useSWRImmutable from "swr/immutable";
import type { Finish } from "../types/finish";
import type { SimpleProduct } from "../types/product";
import type { ShapeInput } from "../types/shape";

/**
 * Is only needed for price calculation
 * @param apiToken
 * @param cartId
 */
export function getFetcher(apiToken?: string, cartId?: string) {
	return async (...args: Parameters<typeof fetch>) => {
		const [url, options] = args;

		const headers = options?.headers ?? ({} as HeadersInit);

		if (apiToken) {
			Object.assign(headers, { "X-API-Key": apiToken });
		}

		if (cartId) {
			Object.assign(headers, { "X-Cart-Id": cartId });
		}

		const response = await fetch(url, {
			...options,
			headers,
		});

		if (!response.ok) throw new Error(response.statusText);

		return response.json();
	};
}

export function useProductInputs(apiUrl: string, productId: string, config?: SWRConfiguration) {
	const response = useSWRImmutable<Record<string, SimpleProduct>>(
		`${apiUrl}/omz_api/catalog_product/products/product_id/${productId}`,
		config,
	);

	return response;
}

export function useShapeInputs(
	apiUrl: string,
	productId?: string,
	postcode?: string | null,
	config?: SWRConfiguration,
) {
	const response = useSWRImmutable<Record<string, ShapeInput>>(
		productId
			? postcode
				? `${apiUrl}/omz_api/catalog_product/shapes/product_id/${productId}?postcode=${postcode}`
				: `${apiUrl}/omz_api/catalog_product/shapes/product_id/${productId}`
			: null,
		config,
	);

	return response;
}

export function useFinishInputs(
	apiUrl: string,
	productId?: string,
	shapeCode?: string,
	config?: SWRConfiguration,
) {
	const response = useSWRImmutable<Record<string, Finish>>(
		productId && shapeCode
			? `${apiUrl}/omz_api/catalog_product/finishes/product_id/${productId}/shape/${shapeCode}`
			: null,
		config,
	);

	return response;
}
